import React, { useState } from 'react';
import { playSpookySound } from './spookyAudio';

/**
 * HalloweenBackgroundGraphics
 * Grand, high-visibility vector SVG background scenery:
 * - Looming Gothic Haunted Citadel with bright glowing candlelit windows
 * - Eerie Harvest Moon with mist swirls and silhouetted bat flocks
 * - Towering gnarled ancient dead trees framing left and right sides
 * - Rolling spooky graveyard ridges with stone Celtic crosses, tombstones & spiked iron gates
 * - Luminous carved Jack-o'-lanterns along the hillsides
 *
 * Placed in background layer (z-0, pointer-events-none), clearly visible through
 * translucent glassmorphic store cards, panels, and sections.
 */
const HalloweenBackgroundGraphics = () => {
  const [flicker, setFlicker] = useState(false);

  const handleCastleClick = (e) => {
    e.stopPropagation();
    playSpookySound('howl');
    setFlicker(true);
    setTimeout(() => setFlicker(false), 1400);
  };

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none">
      {/* 
        ============================================================
        1. DEEP ATMOSPHERIC SKY LIGHTING & GLOW AURA
        ============================================================
      */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 78% 22%, rgba(249, 115, 22, 0.22) 0%, rgba(147, 51, 234, 0.18) 32%, rgba(88, 28, 135, 0.10) 55%, transparent 75%), radial-gradient(circle at 18% 75%, rgba(147, 51, 234, 0.16) 0%, rgba(234, 88, 12, 0.10) 35%, transparent 65%)'
        }}
      />

      {/* 
        ============================================================
        2. FULL PANORAMIC SPOOKY SVG SCENERY (Spans entire screen)
        ============================================================
      */}
      <svg
        viewBox="0 0 1600 1000"
        className="w-full h-full object-cover"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* Gradients */}
          <radialGradient id="skyMoonGrad" cx="42%" cy="42%" r="58%">
            <stop offset="0%" stopColor="#fef08a" />
            <stop offset="35%" stopColor="#fde047" />
            <stop offset="70%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#c2410c" />
          </radialGradient>

          <radialGradient id="moonGlowAura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.55" />
            <stop offset="45%" stopColor="#ea580c" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>

          <linearGradient id="castleRidgeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#24103a" />
            <stop offset="30%" stopColor="#170928" />
            <stop offset="100%" stopColor="#080210" />
          </linearGradient>

          <linearGradient id="midRidgeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1a0b2c" />
            <stop offset="50%" stopColor="#10051d" />
            <stop offset="100%" stopColor="#06010c" />
          </linearGradient>

          <linearGradient id="frontRidgeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#120620" />
            <stop offset="100%" stopColor="#040108" />
          </linearGradient>

          <radialGradient id="castleWindowGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="30%" stopColor="#fef08a" />
            <stop offset="70%" stopColor="#fb923c" />
            <stop offset="100%" stopColor="#d97706" />
          </radialGradient>

          <radialGradient id="jackLanternGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff7ed" />
            <stop offset="40%" stopColor="#fed7aa" />
            <stop offset="75%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#9a3412" />
          </radialGradient>

          <linearGradient id="treeWoodGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e0c33" />
            <stop offset="40%" stopColor="#110520" />
            <stop offset="100%" stopColor="#05010a" />
          </linearGradient>
        </defs>

        {/* ---------------------------------------------------------
            A. ATMOSPHERIC HARVEST MOON IN SKY
            --------------------------------------------------------- */}
        <g transform="translate(1180, 150)">
          {/* Luminous Glow Halo */}
          <circle cx="0" cy="0" r="140" fill="url(#moonGlowAura)" className="animate-pulse" />
          {/* Moon Disc */}
          <circle
            cx="0"
            cy="0"
            r="68"
            fill="url(#skyMoonGrad)"
            stroke="#b45309"
            strokeWidth="1.5"
            className="drop-shadow-[0_0_35px_rgba(249,115,22,0.85)]"
          />
          {/* Moon Surface Craters */}
          <circle cx="-16" cy="-12" r="11" fill="#78350f" opacity="0.22" />
          <circle cx="20" cy="-5" r="14" fill="#78350f" opacity="0.20" />
          <circle cx="-5" cy="22" r="13" fill="#78350f" opacity="0.24" />
          <circle cx="28" cy="25" r="8" fill="#78350f" opacity="0.18" />
          {/* Silhouetted Bats Flying Across Moon Face */}
          <path
            d="M 15 -18 Q 24 -24 33 -19 Q 28 -14 25 -16 Q 22 -12 15 -18 Z"
            fill="#090212"
          />
          <path
            d="M -25 10 Q -18 4 -10 9 Q -14 14 -16 12 Q -19 16 -25 10 Z"
            fill="#090212"
          />
          {/* Eerie Mist Layer Across Face */}
          <path
            d="M -70 12 Q -20 -8 30 18 Q 65 30 75 14"
            stroke="rgba(255, 255, 255, 0.35)"
            strokeWidth="5"
            strokeLinecap="round"
            fill="none"
          />
        </g>

        {/* ---------------------------------------------------------
            B. CIRCLING DISTANT BATS ACROSS THE SKY
            --------------------------------------------------------- */}
        <g fill="#0c0418" opacity="0.9">
          {/* Group 1 (Near Moon) */}
          <path d="M 1020 180 Q 1000 155 980 168 Q 992 182 1006 180 Q 1014 190 1020 180 Z" />
          <path d="M 1060 210 Q 1045 190 1030 200 Q 1040 212 1050 210 Q 1056 218 1060 210 Z" />
          <path d="M 1110 110 Q 1090 90 1070 102 Q 1082 114 1095 112 Q 1102 120 1110 110 Z" />

          {/* Group 2 (Center Sky High) */}
          <path d="M 620 90 Q 595 62 568 80 Q 585 96 603 94 Q 612 105 620 90 Z" className="animate-pulse" />
          <path d="M 670 120 Q 650 98 630 112 Q 644 125 658 123 Q 665 132 670 120 Z" />
          <path d="M 720 85 Q 705 70 690 80 Q 700 90 710 88 Q 715 95 720 85 Z" />

          {/* Group 3 (Left Sky) */}
          <path d="M 280 140 Q 255 115 230 130 Q 245 144 262 142 Q 271 152 280 140 Z" />
          <path d="M 330 165 Q 315 148 300 158 Q 310 168 320 166 Q 325 174 330 165 Z" />
        </g>

        {/* ---------------------------------------------------------
            C. GRAND HAUNTED CITADEL / CASTLE ON MOUNTAIN CRAG (RIGHT)
            --------------------------------------------------------- */}
        <g id="gothic-citadel">
          {/* Mountain Crag Foundation */}
          <path
            d="M 960 1000 
               L 990 620 
               Q 1020 480 1080 390 
               Q 1140 310 1220 320 
               Q 1300 330 1370 420 
               Q 1450 520 1510 680 
               L 1600 1000 Z"
            fill="url(#castleRidgeGrad)"
          />

          {/* THE GOTHIC CASTLE CITADEL */}
          <g
            className="cursor-pointer pointer-events-auto"
            onClick={handleCastleClick}
            title="Click the Haunted Citadel to awaken the ancient spirits! 🏰"
          >
            {/* Main Castle Fortress Keep */}
            <rect x="1150" y="210" width="140" height="120" fill="#0d041a" stroke="#2e104d" strokeWidth="2" />
            {/* Crenellation Battlements */}
            <path
              d="M 1150 210 
                 h 14 v -12 h 14 v 12 h 14 v -12 h 14 v 12 
                 h 14 v -12 h 14 v 12 h 14 v -12 h 14 v 12 
                 h 14 v -12 h 14 v 12 h 4 v 8 h -140 Z"
              fill="#0d041a"
            />

            {/* Left Tower */}
            <rect x="1115" y="175" width="40" height="155" fill="#090214" stroke="#2e104d" strokeWidth="1.5" />
            {/* Left Tower Steep Conical Spire */}
            <polygon points="1108,175 1135,80 1162,175" fill="#06010e" stroke="#2e104d" strokeWidth="1.5" />
            {/* Left Weather Vane */}
            <line x1="1135" y1="80" x2="1135" y2="60" stroke="#f97316" strokeWidth="2" />
            <polygon points="1135,60 1145,65 1135,70" fill="#f97316" />

            {/* Tall Central Cathedral Spire */}
            <rect x="1195" y="140" width="50" height="90" fill="#080212" stroke="#2e104d" strokeWidth="1.5" />
            {/* Needle Peak Spire */}
            <polygon points="1190,140 1220,25 1250,140" fill="#040108" stroke="#3b1366" strokeWidth="1.8" />
            {/* Central Spire Finial Cross */}
            <line x1="1220" y1="25" x2="1220" y2="2" stroke="#fbbf24" strokeWidth="2.5" />
            <line x1="1212" y1="12" x2="1228" y2="12" stroke="#fbbf24" strokeWidth="2" />

            {/* Right Tower */}
            <rect x="1280" y="185" width="38" height="145" fill="#090214" stroke="#2e104d" strokeWidth="1.5" />
            <polygon points="1274,185 1299,95 1324,185" fill="#06010e" stroke="#2e104d" strokeWidth="1.5" />

            {/* Outer Flying Buttresses & Secondary Watchtowers */}
            <path d="M 1090 310 Q 1115 270 1115 220" stroke="#090214" strokeWidth="6" fill="none" />
            <path d="M 1345 310 Q 1320 270 1320 230" stroke="#090214" strokeWidth="6" fill="none" />

            {/* LUMINOUS WARM CANDLELIT GOTHIC ARCH WINDOWS */}
            {/* Left Tower Window */}
            <path
              d="M 1128 220 Q 1135 205 1142 220 L 1142 245 L 1128 245 Z"
              fill="url(#castleWindowGlow)"
              className={flicker ? 'animate-ping' : 'animate-pulse'}
              filter="drop-shadow(0 0 8px rgba(251, 191, 36, 0.95))"
            />
            {/* Central Spire Grand Window */}
            <path
              d="M 1208 175 Q 1220 152 1232 175 L 1232 210 L 1208 210 Z"
              fill="url(#castleWindowGlow)"
              className={flicker ? 'animate-ping' : ''}
              filter="drop-shadow(0 0 12px rgba(251, 146, 60, 0.95))"
            />
            {/* Keep Upper Windows */}
            <rect x="1170" y="240" width="10" height="22" rx="4" fill="url(#castleWindowGlow)" className="animate-pulse" />
            <rect x="1192" y="240" width="10" height="22" rx="4" fill="url(#castleWindowGlow)" />
            <rect x="1240" y="240" width="10" height="22" rx="4" fill="url(#castleWindowGlow)" className="animate-pulse" />
            <rect x="1262" y="240" width="10" height="22" rx="4" fill="url(#castleWindowGlow)" />
            {/* Keep Lower Windows */}
            <rect x="1180" y="280" width="9" height="18" rx="3" fill="url(#castleWindowGlow)" />
            <rect x="1250" y="280" width="9" height="18" rx="3" fill="url(#castleWindowGlow)" className="animate-pulse" />

            {/* Right Tower Window */}
            <path
              d="M 1292 225 Q 1299 212 1306 225 L 1306 250 L 1292 250 Z"
              fill="url(#castleWindowGlow)"
              className={flicker ? 'animate-ping' : 'animate-pulse'}
              filter="drop-shadow(0 0 8px rgba(251, 191, 36, 0.95))"
            />
          </g>
        </g>

        {/* ---------------------------------------------------------
            D. MID-GROUND ROLLING HILLS & HAUNTED GRAVEYARD
            --------------------------------------------------------- */}
        <path
          d="M 0 1000 
             L 0 580 
             Q 280 470 580 540 
             Q 920 610 1240 510 
             Q 1460 440 1600 480 
             L 1600 1000 Z"
          fill="url(#midRidgeGrad)"
        />

        {/* Spiked Gothic Iron Cemetery Fence on Mid-Ground Ridge */}
        <g stroke="#1a092c" strokeWidth="3.5" fill="none">
          <path d="M 340 510 Q 560 535 780 565" />
          <path d="M 340 530 Q 560 555 780 585" />
          {Array.from({ length: 18 }).map((_, i) => {
            const x = 360 + i * 24;
            const y = 495 + (i * 3.8);
            return (
              <g key={i}>
                <line x1={x} y1={y} x2={x} y2={y + 45} />
                <polygon points={`${x - 4},${y + 6} ${x},${y - 6} ${x + 4},${y + 6}`} fill="#1a092c" stroke="none" />
              </g>
            );
          })}
        </g>

        {/* Mid-Ground Cemetery Tombstones & Gothic Celtic Crosses */}
        <g fill="#120520" stroke="#250d3d" strokeWidth="1">
          {/* Celtic Cross on Left Hill */}
          <rect x="220" y="470" width="9" height="55" />
          <rect x="207" y="485" width="35" height="9" />
          <circle cx="224.5" cy="489.5" r="12" fill="none" stroke="#120520" strokeWidth="4" />

          {/* Leaning Headstone (RIP) */}
          <path d="M 290 510 Q 306 480 322 510 L 322 545 L 290 545 Z" />

          {/* Cross 2 */}
          <rect x="860" y="525" width="8" height="50" />
          <rect x="848" y="538" width="32" height="8" />

          {/* Tombstone Slab 3 */}
          <polygon points="915,550 942,542 950,585 923,592" />

          {/* Cross on Far Right */}
          <rect x="1420" y="465" width="9" height="55" />
          <rect x="1407" y="480" width="35" height="9" />
        </g>

        {/* ---------------------------------------------------------
            E. FOREGROUND RIDGE & GLOWING JACK-O'-LANTERNS
            --------------------------------------------------------- */}
        <path
          d="M 0 1000 
             L 0 720 
             Q 400 640 820 730 
             Q 1200 810 1600 690 
             L 1600 1000 Z"
          fill="url(#frontRidgeGrad)"
        />

        {/* GLOWING JACK-O'-LANTERNS NESTLED IN RIDGES */}
        {/* Pumpkin 1 (Left) */}
        <g transform="translate(420, 670) scale(1.35)">
          <ellipse cx="20" cy="18" rx="18" ry="14" fill="#c2410c" stroke="#7c2d12" strokeWidth="1.2" />
          <ellipse cx="20" cy="18" rx="11" ry="13.5" fill="#ea580c" />
          <path d="M 20 4 Q 24 -1 29 0" stroke="#16a34a" strokeWidth="3" fill="none" />
          {/* Luminous Carved Face */}
          <polygon points="13,13 18,16 12,18" fill="url(#jackLanternGlow)" className="animate-pulse" filter="drop-shadow(0 0 6px #f97316)" />
          <polygon points="27,13 22,16 28,18" fill="url(#jackLanternGlow)" className="animate-pulse" filter="drop-shadow(0 0 6px #f97316)" />
          <polygon points="20,16 18,20 22,20" fill="url(#jackLanternGlow)" />
          <path d="M 12 23 L 16 26 L 20 23 L 24 26 L 28 23 L 26 27 L 20 26 L 14 27 Z" fill="url(#jackLanternGlow)" filter="drop-shadow(0 0 8px #fb923c)" />
        </g>

        {/* Pumpkin 2 (Center-Right) */}
        <g transform="translate(980, 715) scale(1.2)">
          <ellipse cx="20" cy="18" rx="18" ry="14" fill="#c2410c" stroke="#7c2d12" strokeWidth="1.2" />
          <ellipse cx="20" cy="18" rx="11" ry="13.5" fill="#ea580c" />
          <path d="M 20 4 Q 16 -1 11 0" stroke="#16a34a" strokeWidth="3" fill="none" />
          <polygon points="13,13 18,16 12,18" fill="url(#jackLanternGlow)" />
          <polygon points="27,13 22,16 28,18" fill="url(#jackLanternGlow)" />
          <path d="M 13 23 Q 20 28 27 23" stroke="url(#jackLanternGlow)" strokeWidth="3" fill="none" className="animate-pulse" filter="drop-shadow(0 0 8px #fb923c)" />
        </g>

        {/* Pumpkin 3 (Far Right) */}
        <g transform="translate(1440, 680) scale(1.25)">
          <ellipse cx="20" cy="18" rx="18" ry="14" fill="#c2410c" stroke="#7c2d12" strokeWidth="1.2" />
          <ellipse cx="20" cy="18" rx="11" ry="13.5" fill="#ea580c" />
          <polygon points="13,13 18,16 12,18" fill="url(#jackLanternGlow)" className="animate-pulse" />
          <polygon points="27,13 22,16 28,18" fill="url(#jackLanternGlow)" className="animate-pulse" />
          <path d="M 12 22 L 16 25 L 20 22 L 24 25 L 28 22 Z" fill="url(#jackLanternGlow)" filter="drop-shadow(0 0 8px #fb923c)" />
        </g>

        {/* ---------------------------------------------------------
            F. TOWERING GNARLED DEAD TREES (LEFT & RIGHT FLANKS)
            --------------------------------------------------------- */}
        {/* LEFT TREE */}
        <g id="left-haunted-tree">
          <path
            d="M -30 1000 
               L -30 750 
               Q 25 700 50 600 
               Q 75 500 60 410 
               Q 45 320 65 240 
               Q 85 160 50 80
               Q 45 65 30 45
               L 42 38
               Q 70 90 78 150
               Q 110 110 150 70
               Q 190 30 210 10
               L 218 16
               Q 190 50 155 100
               Q 185 85 225 90
               L 220 100
               Q 180 95 140 118
               Q 120 165 105 220
               Q 160 190 220 178
               Q 280 165 330 120
               L 336 128
               Q 285 180 225 200
               Q 265 200 320 225
               L 315 235
               Q 255 210 205 212
               Q 165 245 125 300
               Q 195 285 270 315
               Q 330 340 375 320
               L 378 330
               Q 325 355 255 330
               Q 185 315 110 345
               Q 95 440 105 550
               Q 170 520 230 560
               L 225 570
               Q 165 535 102 568
               Q 100 670 130 750
               Q 160 820 215 900
               L 195 1000 Z"
            fill="url(#treeWoodGrad)"
            stroke="#2e104d"
            strokeWidth="2"
          />

          {/* Perched Owl with Luminous Golden Eyes */}
          <g transform="translate(150, 205) scale(1.3)">
            <ellipse cx="12" cy="14" rx="8" ry="12" fill="#080112" />
            <circle cx="12" cy="5" r="6" fill="#080112" />
            <polygon points="7,2 5,-3 10,0" fill="#080112" />
            <polygon points="17,2 19,-3 14,0" fill="#080112" />
            <polygon points="8,24 16,24 12,32" fill="#080112" />
            {/* Luminous Glowing Eyes */}
            <circle cx="10" cy="5" r="1.8" fill="#fde047" className="animate-pulse" filter="drop-shadow(0 0 4px #eab308)" />
            <circle cx="14" cy="5" r="1.8" fill="#fde047" className="animate-pulse" filter="drop-shadow(0 0 4px #eab308)" />
          </g>
        </g>

        {/* RIGHT TREE / OVERHANGING SPOOKY BOUGH */}
        <g id="right-haunted-tree">
          <path
            d="M 1630 -30 
               L 1550 -30 
               Q 1510 50 1470 120 
               Q 1410 200 1340 240 
               Q 1280 270 1220 285
               L 1217 275
               Q 1270 260 1320 235
               Q 1280 215 1240 190
               L 1244 180
               Q 1290 205 1335 225
               Q 1380 175 1440 110
               Q 1370 115 1310 95
               L 1313 85
               Q 1375 105 1445 95
               Q 1490 40 1540 -30 Z"
            fill="url(#treeWoodGrad)"
            stroke="#2e104d"
            strokeWidth="2"
          />
        </g>
      </svg>
    </div>
  );
};

export default HalloweenBackgroundGraphics;
